import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/httpClient';
import {
  getCommunicationAttachmentUrl,
  getCommunications,
  postCommunication,
  getPeopleCached,
  getPartnerOrgMembersCached,
  filterPeople,
  type CachedPerson,
  type CommunicationMessage,
  type TaggedPerson
} from '../../api/vipMpApi';
import { useToast } from '../../context/ToastContext';
import { ScanningOverlay } from './ScanningOverlay';

const PERSONA_API_CODE: Record<string, string> = {
  partner: 'PARTNER',
  om: 'OM',
  cam: 'CAM',
  bpops: 'BP_OPS',
  bp_ops: 'BP_OPS'
};

const PERSONA_LABEL: Record<string, string> = {
  PARTNER: 'Partner',
  OM:      'OM Contact',
  CAM:     'Adobe Account Manager',
  BP_OPS:  'Adobe Team',
};

const PERSONA_COLOR: Record<string, string> = {
  PARTNER: '#1565c0',
  OM:      '#2e7d32',
  CAM:     '#6a1b9a',
  BP_OPS:  '#e65100',
};

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

type PendingFile = { name: string; type: string; dataUrl: string };

type CaseContact = { name: string; email: string; role: 'submitter' | 'partner_contact' };

type Props = {
  returnRequestId: number;
  currentPersona: 'partner' | 'om' | 'cam' | 'bpops' | 'bp_ops';
  currentUser: { name: string; email: string };
  caseContacts?: CaseContact[];
};

function PersonaBadge({ code }: { code: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 10,
      fontSize: 11, fontWeight: 600, color: '#fff',
      background: PERSONA_COLOR[code] || '#666', marginRight: 4
    }}>
      {PERSONA_LABEL[code] || code}
    </span>
  );
}

function MentionChip({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      background: '#e3f2fd', borderRadius: 12, padding: '2px 8px',
      fontSize: 12, color: '#1565c0', fontWeight: 500
    }}>
      @{name}
      <button
        type="button"
        onClick={onRemove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1565c0', padding: 0, lineHeight: 1, fontSize: 14, marginLeft: 1 }}
        aria-label={`Remove @${name}`}
      >×</button>
    </span>
  );
}

function renderWithMentions(text: string | undefined | null) {
  if (!text) return null;
  const parts = text.split(/(@[A-Za-z][^\s@]*(?:\s[A-Z][^\s@,!?.:\n]*)*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <strong key={i} style={{ color: '#1565c0', fontWeight: 600 }}>{part}</strong>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function formatTs(utc: string): string {
  try {
    return new Date(utc).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return utc;
  }
}

function dataUrlToBase64(dataUrl: string): string {
  const idx = dataUrl.indexOf(',');
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

export function CommunicationPanel({ returnRequestId, currentPersona, currentUser, caseContacts = [] }: Props) {
  const { showToast } = useToast();
  const authorCode = PERSONA_API_CODE[currentPersona] || PERSONA_API_CODE.partner;

  const [iamList, setIamList]           = useState<CachedPerson[]>([]);
  const [messages, setMessages]         = useState<CommunicationMessage[]>([]);
  const [loading, setLoading]           = useState(false);
  const [sending, setSending]           = useState(false);
  const [text, setText]                 = useState('');
  const [taggedPeople, setTaggedPeople] = useState<TaggedPerson[]>([]);
  const [files, setFiles]               = useState<PendingFile[]>([]);

  // @mention dropdown state
  const [mentionQuery, setMentionQuery]     = useState('');
  const [mentionAtPos, setMentionAtPos]     = useState<number | null>(null);
  const [mentionResults, setMentionResults] = useState<TaggedPerson[]>([]);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [selectedIdx, setSelectedIdx]       = useState(-1);

  const textareaRef  = useRef<HTMLTextAreaElement>(null);
  const threadRef    = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchTimer  = useRef<ReturnType<typeof setTimeout>>();

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCommunications(returnRequestId, {
        requesterPersona: authorCode,
        requesterEmail: currentUser.email
      });
      setMessages(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 500) return;
      showToast(err instanceof ApiError ? err.message : 'Failed to load communications', 'error');
    } finally {
      setLoading(false);
    }
  }, [returnRequestId, authorCode, currentUser.email, showToast]);

  useEffect(() => { void loadMessages(); }, [loadMessages]);

  useEffect(() => {
    getPeopleCached().then(setIamList).catch(() => {});
  }, []);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  // Detect @mention context from textarea value and cursor
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const cursor = e.target.selectionStart ?? val.length;
    setText(val);

    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');

    if (atIdx >= 0 && (atIdx === 0 || /\s/.test(before[atIdx - 1]))) {
      const query = before.slice(atIdx + 1);
      // A space in the query means the mention was already completed — don't re-open
      if (!query.includes(' ') && !query.includes('\n')) {
        setMentionAtPos(atIdx);
        setMentionQuery(query);
        setShowDropdown(true);
        triggerSearch(query);
        return;
      }
    }

    setShowDropdown(false);
    setMentionAtPos(null);
    setMentionQuery('');
  };

  const triggerSearch = (query: string) => {
    clearTimeout(searchTimer.current);
    const qLower = query.toLowerCase();
    const pinned = caseContacts
      .filter(c => !query || c.name.toLowerCase().includes(qLower) || c.email.toLowerCase().includes(qLower))
      .map(c => ({ name: c.name, email: c.email }));

    if (query.length < 1) {
      setMentionResults(pinned);
      setSelectedIdx(-1);
      setDropdownLoading(false);
      return;
    }
    setDropdownLoading(true);
    setMentionResults(pinned);
    searchTimer.current = setTimeout(async () => {
      try {
        // Partners get OM/CAM from IAM (no BP_OPS) + same-org partner submitters from DB
        // Non-partner personas get IAM only
        const [iamList, orgList] = await Promise.all([
          getPeopleCached(),
          currentPersona === 'partner' ? getPartnerOrgMembersCached() : Promise.resolve([])
        ]);
        const merged = [...iamList];
        const seenEmails = new Set(iamList.map(p => p.email.toLowerCase()));
        for (const p of orgList) {
          if (!seenEmails.has(p.email.toLowerCase())) merged.push(p);
        }
        const results = filterPeople(merged, query);
        const pinnedEmails = new Set(pinned.map(p => p.email.toLowerCase()));
        setMentionResults([...pinned, ...results.filter(r => !pinnedEmails.has(r.email.toLowerCase()))]);
        setSelectedIdx(-1);
      } catch {
        setMentionResults(pinned);
        setSelectedIdx(-1);
      } finally {
        setDropdownLoading(false);
      }
    }, 250);
  };

  const insertMention = (person: TaggedPerson) => {
    if (mentionAtPos === null || !textareaRef.current) return;
    const cursor = textareaRef.current.selectionStart ?? text.length;
    const before = text.slice(0, mentionAtPos);
    const after = text.slice(cursor);
    const mention = `@${person.name}`;
    const newText = `${before}${mention} ${after}`;
    setText(newText);

    if (!taggedPeople.some(p => p.email === person.email)) {
      setTaggedPeople(prev => [...prev, person]);
    }

    setShowDropdown(false);
    setMentionResults([]);
    setMentionAtPos(null);
    setMentionQuery('');
    setSelectedIdx(-1);

    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + mention.length + 1;
        textareaRef.current.setSelectionRange(pos, pos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showDropdown || mentionResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, mentionResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIdx >= 0) {
      e.preventDefault();
      insertMention(mentionResults[selectedIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIdx(-1);
    }
  };

  const removePerson = (email: string) => {
    setTaggedPeople(prev => prev.filter(p => p.email !== email));
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (files.length + picked.length > MAX_FILES) {
      showToast(`Maximum ${MAX_FILES} files per message`, 'error');
      return;
    }
    for (const f of picked) {
      if (f.size > MAX_FILE_BYTES) {
        showToast(`${f.name} exceeds 10 MB limit`, 'error');
        return;
      }
    }
    picked.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => {
        setFiles(prev => [...prev, { name: f.name, type: f.type, dataUrl: String(ev.target?.result || '') }]);
      };
      reader.readAsDataURL(f);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async () => {
    if (!text.trim()) { showToast('Message cannot be empty', 'error'); return; }
    if (taggedPeople.length === 0) { showToast('Tag at least one person using @name', 'error'); return; }
    setSending(true);
    try {
      await postCommunication(returnRequestId, {
        authorPersonaCode: authorCode,
        authorName: currentUser.name,
        authorEmail: currentUser.email,
        messageText: text.trim(),
        taggedPeople,
        attachments: files.map(f => ({
          fileName: f.name,
          fileType: f.type,
          fileContentBase64: dataUrlToBase64(f.dataUrl)
        }))
      });
      setText('');
      setTaggedPeople([]);
      setFiles([]);
      await loadMessages();
      showToast('Message sent', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (storageKey: string, fileName: string) => {
    try {
      const { url } = await getCommunicationAttachmentUrl(returnRequestId, storageKey, {
        requesterPersona: authorCode,
        requesterEmail: currentUser.email
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      a.click();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Download failed', 'error');
    }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      {sending && files.length > 0 && <ScanningOverlay message="Scanning attachment(s) for viruses…" />}
      <div className="card-header">
        <h2>Communications</h2>
      </div>
      <div className="card-body" style={{ padding: '12px 16px' }}>

        {/* Message thread */}
        <div
          ref={threadRef}
          style={{ maxHeight: 340, overflowY: 'auto', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {loading && <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>}
          {!loading && messages.length === 0 && (
            <p style={{ color: '#888', fontSize: 13 }}>No messages yet. Start a conversation below.</p>
          )}
          {messages.map(msg => (
            <div
              key={msg.communicationId}
              style={{
                border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px',
                background: msg.authorPersonaCode === authorCode ? '#f0f7ff' : '#fafafa'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <PersonaBadge code={msg.authorPersonaCode} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{msg.authorName}</span>
                <span style={{ color: '#888', fontSize: 12 }}>{formatTs(msg.createdUtc)}</span>
                {msg.taggedPeople?.length > 0 && (
                  <span style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    →
                    {msg.taggedPeople.map(p => (
                      <span key={p.email} style={{
                        background: '#e3f2fd', borderRadius: 10, padding: '1px 7px',
                        fontSize: 11, fontWeight: 600, color: '#1565c0'
                      }}>
                        @{p.name}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: 13, whiteSpace: 'pre-wrap', color: '#222' }}>
                {renderWithMentions(msg.messageText ?? (msg as Record<string, unknown>)['MessageText'] as string)}
              </p>
              {msg.attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {msg.attachments.map((att, i) => (
                    <button
                      key={i}
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 12 }}
                      onClick={() => void downloadAttachment(att.storageKey, att.fileName)}
                    >
                      📎 {att.fileName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Compose */}
        <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 12 }}>

          {/* Tagged people chips */}
          {taggedPeople.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#555', marginRight: 2 }}>Tagged:</span>
              {taggedPeople.map(p => (
                <MentionChip key={p.email} name={p.name} onRemove={() => removePerson(p.email)} />
              ))}
            </div>
          )}

          {/* Textarea + dropdown */}
          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              className="form-control"
              rows={3}
              placeholder="Write a message… type @ to mention someone"
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => { setShowDropdown(false); setSelectedIdx(-1); }, 150)}
              style={{ marginBottom: 6, resize: 'vertical' }}
            />

            {/* @mention dropdown */}
            {showDropdown && (
              <div style={{
                position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto'
              }}>
                {dropdownLoading && (
                  <div style={{ padding: '8px 12px', fontSize: 13, color: '#888' }}>Searching…</div>
                )}
                {!dropdownLoading && mentionQuery.length >= 1 && mentionResults.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 13, color: '#888' }}>No people found for "{mentionQuery}"</div>
                )}
                {!dropdownLoading && mentionQuery.length < 1 && mentionResults.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 13, color: '#888' }}>Keep typing to search…</div>
                )}
                {!dropdownLoading && mentionQuery.length < 1 && mentionResults.length > 0 && (
                  <div style={{ padding: '4px 12px 2px', fontSize: 11, color: '#888', fontWeight: 600 }}>Case contacts — type to search more people</div>
                )}
                {mentionResults.map((person, idx) => {
                  const caseContact = caseContacts.find(c => c.email.toLowerCase() === person.email.toLowerCase());
                  const iamPerson   = iamList.find(p => p.email.toLowerCase() === person.email.toLowerCase());
                  let badge: string | null = null;
                  let badgeBg = '#1565c0';
                  if (caseContact?.role === 'partner_contact') { badge = 'Case OM Contact'; badgeBg = '#2e7d32'; }
                  else if (caseContact?.role === 'submitter')  { badge = 'Partner Contact'; badgeBg = '#1565c0'; }
                  else if (iamPerson?.persona === 'om')        { badge = 'OM Contact';      badgeBg = '#2e7d32'; }
                  else if (iamPerson?.persona === 'cam')       { badge = 'Adobe Account Manager'; badgeBg = '#6a1b9a'; }
                  return (
                    <div
                      key={person.email}
                      onMouseDown={() => insertMention(person)}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      onMouseLeave={() => setSelectedIdx(-1)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                        display: 'flex', flexDirection: 'column', gap: 1,
                        background: idx === selectedIdx ? '#e3f2fd' : ''
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {person.name}
                        {badge && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: '#fff', background: badgeBg, borderRadius: 8, padding: '1px 6px', verticalAlign: 'middle' }}>
                            {badge}
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 11, color: '#888' }}>{person.email}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <p style={{ fontSize: 11, color: '#999', margin: '0 0 10px 0' }}>
            Tip: type <strong>@</strong> followed by a name to tag someone
          </p>

          {/* File attachments */}
          <div style={{ marginBottom: 10 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.msg,.csv"
              onChange={onFileChange}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_FILES}
              style={{ marginRight: 8 }}
            >
              Attach file
            </button>
            {files.map((f, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: '#e8f5e9', borderRadius: 4, padding: '2px 8px',
                  fontSize: 12, marginRight: 6, marginTop: 4
                }}
              >
                {f.name}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', padding: 0, lineHeight: 1 }}
                >×</button>
              </span>
            ))}
          </div>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void handleSend()}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send message'}
          </button>
        </div>
      </div>
    </div>
  );
}
